import { Alert, App, Button, Card, Descriptions, Input, Space, Spin, Tabs, Tag, Typography } from 'antd';
import {
  KeyOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  MessageOutlined,
  IdcardOutlined,
  MobileOutlined,
  NotificationOutlined,
  SendOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../../api/client';
import { NamespaceConfigForm } from './components/NamespaceConfigForm';
import type { NamespaceConfig } from './components/NamespaceConfigForm';

interface ProviderTab {
  key: string;
  label: string;
  onlyKeys: string[];
  guide: string;
}

function useConfig(name: string) {
  return useQuery({
    queryKey: ['system-config', name],
    queryFn: async () => (await get<unknown>(`/admin/system-config/${name}`)) as NamespaceConfig,
  });
}

function CredentialSection({
  name,
  onlyKeys,
  desc,
}: {
  name: string;
  onlyKeys: string[];
  desc: string;
}) {
  const { data, isLoading } = useConfig(name);
  if (isLoading || !data) return <Spin />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert type="info" showIcon title={desc} />
      <NamespaceConfigForm config={data} onlyKeys={onlyKeys} ignoreShowWhen />
    </div>
  );
}

function SmtpCredentialPanel() {
  const { data, isLoading } = useConfig('smtp');
  const { message } = App.useApp();
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  if (isLoading || !data) return <Spin />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert
        type="info"
        showIcon
        title="邮件 SMTP 全部连接参数集中在此维护"
        description="系统邮件（注册邮箱验证码、密码重置、客户档案通知）与「留言通知 → 邮件」转发共用这套 SMTP。发件密码/授权码在邮箱服务商后台生成、不是邮箱登录密码，加密存储、回显脱敏。填好保存后可用右上角「发送测试邮件」验证链路。"
      />
      <NamespaceConfigForm
        config={data}
        cardTitle="邮件 SMTP"
        extraActions={
          <>
            <Input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="测试邮箱"
              style={{ width: 180 }}
              type="email"
            />
            <Button
              icon={<SendOutlined />}
              loading={testing}
              onClick={async () => {
                if (!testEmail) {
                  message.warning('请填写测试邮箱地址');
                  return;
                }
                setTesting(true);
                try {
                  const resp = await post<{ success: boolean; message: string }>('/admin/system-config/smtp/test', {
                    email: testEmail,
                  });
                  if (resp.success) message.success(resp.message);
                  else message.error(resp.message);
                } catch (error) {
                  message.error(error instanceof Error ? error.message : '测试失败');
                } finally {
                  setTesting(false);
                }
              }}
            >
              发送测试邮件
            </Button>
          </>
        }
      />
    </div>
  );
}

function NotifyEventSwitch() {
  const { data, isLoading } = useConfig('notify');
  if (isLoading || !data) return <Spin />;
  return (
    <NamespaceConfigForm
      config={data}
      onlyKeys={['applicationEnabled', 'transferEnabled']}
      switchCardKeys={['applicationEnabled', 'transferEnabled']}
      ignoreShowWhen
      cardTitle="通知事件"
      emptyHint="暂无可勾选的通知事件"
    />
  );
}

function ProviderPanel({
  config,
  provider,
}: {
  config: NamespaceConfig;
  provider: ProviderTab;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert type="info" showIcon title="配置方法" description={provider.guide} />
      <NamespaceConfigForm
        config={config}
        onlyKeys={provider.onlyKeys}
        ignoreShowWhen
        cardTitle={`${provider.label} 凭据`}
      />
    </div>
  );
}

function ProviderTabsSection({
  intro,
  configName,
  providers,
}: {
  intro: string;
  configName: string;
  providers: ProviderTab[];
}) {
  const { data, isLoading } = useConfig(configName);
  if (isLoading || !data) return <Spin />;
  // 只有一个运营商时不再套一层空 tab，直接平铺；多运营商才分 tab。
  if (providers.length === 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Alert type="info" showIcon title={intro} />
        <ProviderPanel config={data} provider={providers[0]} />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert type="info" showIcon title={intro} />
      <Tabs
        type="card"
        items={providers.map((p) => ({
          key: p.key,
          label: p.label,
          children: <ProviderPanel config={data} provider={p} />,
        }))}
      />
    </div>
  );
}

const SMS_PROVIDERS: ProviderTab[] = [
  {
    key: 'aliyun',
    label: '阿里云',
    onlyKeys: ['aliyunAccessKeyId', 'aliyunAccessKeySecret'],
    guide:
      '1. 阿里云控制台 → RAM 访问控制 → 用户 → 创建 AccessKey，获取 AccessKeyId 与 AccessKeySecret；2. 短信服务 → 国内消息 → 签名管理（审核通过）→ 模板管理（验证码模板需含 ${code} 变量）。签名与模板在注册流程页「手机号持有验证」步配置。',
  },
  // 腾讯云短信没有标签页：后端 sms.service 只有 `// TODO: 实际调用腾讯云 SMS SDK`，
  // 选中它等于验证码短信一条都发不出去，故连 provider 选项带密钥字段一并从 sms 组移除。
  // 实名认证同理（服务层那段 tencent 是把身份证号拼进 URL 的伪实现），系统真实渠道是阿里云三要素、
  // 阿里云市场与新诺维尔三条。
];

const IDENTITY_PROVIDERS: ProviderTab[] = [
  {
    key: 'aliyun',
    label: '阿里云',
    onlyKeys: ['accessKeyId', 'accessKeySecret', 'apiEndpoint', 'regionId'],
    guide:
      '1. 阿里云控制台 → RAM → 创建 AccessKey；2. 开通「号码认证 / 实人认证」服务（三要素核验）；3. 填入 AccessKeyId / AccessKeySecret，地域按开通服务时选的那个填（默认 cn-hangzhou）。本组凭据与地域只对「阿里云直连」这一条渠道生效，阿里云市场、newnorwell 各有自己的标签页。请求超时是三条渠道共用的参数，在注册流程页「实名核验」步配置。',
  },
  {
    key: 'aliyunmarket',
    label: '阿里云市场',
    onlyKeys: ['marketEndpoint', 'marketAppCode'],
    guide:
      '1. 在阿里云市场购买「手机三要素」类 API 商品（按次计费，注意套餐包额度，额度用尽后核验会返回错误码、一律不通过）；2. 控制台「已购买的服务」→ 该商品 →「接口文档」，把「调用地址」整条（含路径）复制进下方；3. 同页「获取认证」里只取 AppCode 填进下方——控制台会并列展示 AppKey / AppSecret / AppCode 三件，本渠道走云市场的 AppCode 简单认证（请求头 Authorization: APPCODE <值>），AppKey 与 AppSecret 都不需要填；只有改走网关签名认证时才用它们，而系统当前没有实现那条。4. 回到注册流程页「实名核验」把认证服务商切到 aliyunmarket 并启用。此渠道按云市场通用契约调用：姓名/身份证号/手机号三项以 GET Query 送出，返回体 data.result 为 0 才算一致通过。',
  },
  {
    key: 'newnorwell',
    label: 'newnorwell',
    onlyKeys: ['appId', 'appKey'],
    guide: '1. 联系 newnorwell 服务商开通三要素核验服务；2. 获取 AppId 与 AppKey；3. 填入下方。',
  },
];

const CHANNEL_LABEL: Record<string, string> = {
  aliyun: '阿里云直连',
  aliyunmarket: '阿里云市场',
  newnorwell: 'newnorwell',
  test: 'test（本地调试通道）',
  local: '本地验证（不调第三方）',
};

interface ChannelCheck {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
}

type ChannelOutcome = 'config_incomplete' | 'verdict_pass' | 'verdict_fail' | 'call_error';

interface ChannelTestResult {
  outcome: ChannelOutcome;
  provider: string;
  checks: ChannelCheck[];
  passed: boolean;
  called: boolean;
  success?: boolean;
  message?: string;
  providerResponse?: unknown;
  elapsedMs?: number;
}

/**
 * 四档各说各的故障，不合并（10-13 裁定）：
 * 「配置没填齐」不花钱、「渠道答上了但三要素不一致」是数据问题、「渠道压根没答上」才是通道故障，
 * 早年这里只看 success，把后两档挤成同一个红 Tag，照着排查会去改密钥而真正该改的是配置。
 */
const OUTCOME_PRESENTATION: Record<ChannelOutcome, { stage: string; tag: string; color: string }> = {
  config_incomplete: {
    stage: '停在预检，未出网、未产生费用',
    tag: '配置未齐',
    color: 'orange',
  },
  verdict_pass: {
    stage: '预检通过，已真实调用服务商',
    tag: '渠道可用，核验一致',
    color: 'green',
  },
  verdict_fail: {
    stage: '预检通过，已真实调用服务商',
    tag: '渠道可用，三要素不一致/无记录',
    color: 'blue',
  },
  call_error: {
    stage: '预检通过，已发起调用但服务商未正常应答',
    tag: '渠道故障',
    color: 'red',
  },
};

/**
 * 实名核验渠道测试（两档合一，10-13 加）。
 *
 * 只有一张卡、不按标签页复制：后端读的是**当前生效配置**里的 provider，不是你这个标签页正在看的那家。
 * 所以卡标题右侧直接把「本次会走哪条渠道」打出来，标签页只负责填凭据。
 */
function IdentityChannelTestCard() {
  const { data, isLoading } = useConfig('identity');
  const { message } = App.useApp();
  const [fullName, setFullName] = useState('');
  const [idCard, setIdCard] = useState('');
  const [phone, setPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<ChannelTestResult | null>(null);

  if (isLoading || !data) return <Spin />;
  const liveProvider = String(data.values.provider ?? '');

  const shapeError =
    !fullName.trim() || !idCard.trim() || !phone.trim()
      ? '三项都要填'
      : !/^\d{17}[\dXx]$/.test(idCard.trim())
        ? '身份证号要 18 位'
        : !/^1\d{10}$/.test(phone.trim())
          ? '手机号要 11 位、1 开头'
          : null;

  // 两端分开部署：admin 先上线时旧后端的响应里没有 outcome，退回按 called/passed 描述，至少不是一张空标签
  const presentation = result
    ? OUTCOME_PRESENTATION[result.outcome] ?? {
        stage: result.called ? '已发起调用（后端未回结果档位）' : '停在预检，未出网、未产生费用',
        tag: result.called ? (result.success ? '核验一致' : '核验未通过') : '未调用',
        color: 'default',
      }
    : null;

  return (
    <Card title="测试这条渠道是否可用" extra={
      <Tag color={liveProvider === 'test' ? 'default' : 'blue'}>
        当前生效：{CHANNEL_LABEL[liveProvider] ?? (liveProvider || '未配置')}
      </Tag>
    }>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          title="一次调用做两件事：先查配置，配置齐了才真发一次核验"
          description="预检只读本地配置、不花钱，逐项指出缺哪一格：少填、填成回显的脱敏值（**** 开头）、服务商还停在 test、「模拟验证不通过」开关没关，都会在这一档被拦下并说明去哪儿改。预检全通过才会真的向服务商发一次三要素核验，那一次是按次计费的，故本口限速每分钟 5 次。三要素请用自己确认可查的测试身份。测试刻意不往「实名核验记录」落流水：那条表一旦被写成成功记录，注册闸门会按「同三要素 15 分钟内已验证」直接放行，等于替这个身份把门打开。"
        />
        <Space wrap size={8}>
          <Input
            placeholder="姓名"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={{ width: 140 }}
            maxLength={50}
          />
          <Input
            placeholder="身份证号（18 位）"
            value={idCard}
            onChange={(e) => setIdCard(e.target.value)}
            style={{ width: 210 }}
            maxLength={18}
          />
          <Input
            placeholder="手机号（11 位）"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={{ width: 170 }}
            maxLength={20}
          />
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={testing}
            disabled={Boolean(shapeError)}
            title={shapeError ?? undefined}
            onClick={async () => {
              setTesting(true);
              setResult(null);
              try {
                const resp = await post<ChannelTestResult>('/identity/test', {
                  fullName: fullName.trim(),
                  idCard: idCard.trim(),
                  phone: phone.trim(),
                });
                setResult(resp);
              } catch (error) {
                message.error(error instanceof Error ? error.message : '渠道测试请求失败');
              } finally {
                setTesting(false);
              }
            }}
          >
            测试渠道
          </Button>
        </Space>
        {result && presentation && (
          <div style={{ border: '1px solid #e6f0ee', borderRadius: 12, padding: '12px 14px' }}>
            <Descriptions size="small" column={1} style={{ marginBottom: 10 }}>
              <Descriptions.Item label="走的是哪条">
                {CHANNEL_LABEL[result.provider] ?? result.provider}
              </Descriptions.Item>
              <Descriptions.Item label="阶段">{presentation.stage}</Descriptions.Item>
              <Descriptions.Item label="判决">
                <Tag color={presentation.color}>{presentation.tag}</Tag>
              </Descriptions.Item>
              {result.message && <Descriptions.Item label="服务商原话">{result.message}</Descriptions.Item>}
              {typeof result.elapsedMs === 'number' && (
                <Descriptions.Item label="耗时">{result.elapsedMs} ms</Descriptions.Item>
              )}
            </Descriptions>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {result.checks.map((c) => (
                <div key={c.key} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <Tag color={c.ok ? 'green' : 'red'} style={{ minWidth: 56, textAlign: 'center' }}>
                    {c.ok ? '通过' : '未通过'}
                  </Tag>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {c.label}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
                    {c.detail}
                  </Typography.Text>
                </div>
              ))}
            </Space>
            {result.providerResponse !== undefined && result.providerResponse !== null && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13 }}>服务商返回内容（已按值掩码身份证与手机号）</summary>
                <Typography.Paragraph
                  copyable
                  style={{
                    marginTop: 6,
                    marginBottom: 0,
                    padding: '8px 10px',
                    background: '#f7f8f8',
                    borderRadius: 8,
                    fontSize: 12.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(result.providerResponse, null, 2)}
                </Typography.Paragraph>
              </details>
            )}
          </div>
        )}
      </Space>
    </Card>
  );
}

const NOTIFY_CHANNELS: ProviderTab[] = [
  {
    key: 'wecom',
    label: '企业微信',
    onlyKeys: ['wecomEnabled', 'wecomWebhook'],
    guide:
      '1. 在企业微信群里点群名 → 聊天信息 → 消息推送 → 添加，把机器人命名为「实时通知」；2. 添加成功后复制 Webhook 地址（https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=…）填入下方；3. 打开上方「启用企业微信通知」开关保存即生效。Webhook 地址本身就是凭据，加密存储、保存后回显为脱敏值。留言、待审申请与转账待核对共用这一个群，按需在上方「通知事件」勾选要推哪些。',
  },
  {
    key: 'dingtalk',
    label: '钉钉',
    onlyKeys: ['dingtalkEnabled', 'dingtalkWebhook', 'dingtalkSecret'],
    guide:
      '1. 钉钉群 → 群设置 → 智能群助手 → 添加机器人 → 自定义；2. 安全设置任选其一：勾选「加签」则复制 SEC 开头的密钥填入「加签密钥」；若选「自定义关键词」，因留言与待审申请标题不同，请把关键词设为两者都含的词（如「通知」）；3. 复制 Webhook 地址（https://oapi.dingtalk.com/robot/send?access_token=…）填入；4. 打开「启用钉钉通知」开关保存。',
  },
  {
    key: 'feishu',
    label: '飞书',
    onlyKeys: ['feishuEnabled', 'feishuWebhook', 'feishuSecret'],
    guide:
      '1. 飞书群 → 设置 → 群机器人 → 添加机器人 → 自定义机器人；2. 安全设置若勾选「签名校验」，复制生成的密钥填入「签名校验密钥」，未开启则留空；3. 复制 Webhook 地址（https://open.feishu.cn/open-apis/bot/v2/hook/…）填入；4. 打开「启用飞书通知」开关保存。',
  },
  {
    key: 'email',
    label: '邮件',
    onlyKeys: ['emailEnabled', 'emailTo'],
    guide:
      '被勾选的事件（留言 / 待审申请）通过系统 SMTP（全部连接参数含发件账号、密码/授权码在「邮件 SMTP」标签页维护）转发到指定运营邮箱。1. 填写接收邮箱（多个用英文逗号分隔）；2. 打开「启用邮件转发通知」开关保存。只开开关不填邮箱则邮件通道不发送。',
  },
];

/**
 * 推送按「厂商 + 端」分 tab。
 *
 * 之前这页只列了华为的 6 个字段（写死在 onlyKeys 里），OPPO / vivo / 小米接入后
 * 后台**根本没有入口填它们的凭据**——新通道等于不可配置。现在每家一个 tab，
 * 字段与后端 namespaces.ts 的 push 命名空间一一对应，新增厂商只需在这里加一项。
 */
const PUSH_PROVIDERS: ProviderTab[] = [
  {
    key: 'huawei-v3',
    label: '华为（鸿蒙 NEXT）',
    onlyKeys: ['projectId', 'keyId', 'subAccount', 'privateKey'],
    guide:
      '1. 华为开发者联盟 API Console → 创建「服务账号密钥」凭证 → 下载 JSON 文件；2. 按 JSON 字段填入 Key ID / Sub Account / Private Key；3. Project ID 取自 AGC → 项目设置 → 常规 → 项目 ID（**不是 AppId**）。本组服务鸿蒙 NEXT 设备（hmos 工程），与右侧「安卓 HMS」是两条互不相通的通道。⚠️ 鸿蒙端另需在 hmos 工程的 module.json5 写死 client_id——它由系统推送服务读取，**无法在后台配置**（与 vivo 的 Manifest 凭据同理），详见 hmos/PUSH_SETUP.md。',
  },
  {
    key: 'huawei-v1',
    label: '华为（安卓 HMS）',
    onlyKeys: ['appId', 'appSecret'],
    guide:
      '1. AGC → 项目设置 → 常规 → 应用信息，取 AppId 与 Client Secret；2. 填入下方。本组服务 Android 端的华为 / 分家前的老荣耀机型（app_flutter 工程，需在 android/app/libs 放入 HMS Core Push SDK 的 AAR）。appId 会经 /public/config 下发到 App 用于换取 AAID token；appSecret 仅服务端签名用、不下发到客户端。新荣耀 MagicOS 没有 HMS Core，走不到这条通道。',
  },
  {
    key: 'honor',
    label: '荣耀（MagicOS 8.0+）',
    onlyKeys: ['honorAppId', 'honorClientId', 'honorClientSecret'],
    guide:
      '荣耀 2020 年底从华为分家后，开发者平台、SDK、凭据、服务端 API 全部独立——华为那两组凭据在荣耀侧一律不认，必须单独开通。1. 荣耀开发者服务平台 → 应用管理 → 新建应用并绑定包名；2. 开发能力 → 推送服务 → 申请，填写**打包证书的 SHA256 指纹**（debug / release 各一份，与出包证书必须一致）；3. 开通后取 App ID、Client ID、Client Secret 填入下方。⚠️ 三个值别填混：App ID 用于下发 URL 与客户端 Manifest，Client ID/Secret 只用于服务端换 Access Token，填反的表现是「能换到 token，但下发一律 80300007」。⚠️ 客户端那份 app_id **无法后台下发**（SDK 从 AndroidManifest 读），必须另外写进 android/local.properties 的 honor.push.appId 并重新出包。⚠️ 覆盖机型只有 MagicOS 8.0 及以上，8.0 以下继续走上面的华为通道；两条通道在同一份 APK 里共存，不需要分包。另：未申请「自分类权益」时荣耀限制每设备每天约 2~5 条，订单类应用务必去申请。',
  },
  {
    key: 'oppo',
    label: 'OPPO / 一加 / realme',
    onlyKeys: ['oppoAppKey', 'oppoAppSecret', 'oppoMasterSecret'],
    guide:
      '1. OPPO 推送平台 → 配置管理 → 应用配置，取 AppKey、AppSecret、MasterSecret；2. 需先在平台申请「消息推送」权限（测试权限即可联调）。⚠️ AppSecret（客户端 SDK register 用，会下发到 App）与 MasterSecret（服务端签名用）是**两个不同的值**，填反的表现是服务端一直回「签名校验不通过」。',
  },
  {
    key: 'vivo',
    label: 'vivo',
    onlyKeys: ['vivoAppId', 'vivoAppKey', 'vivoAppSecret'],
    guide:
      '1. vivo 开放平台 → 推送 → 应用信息，取 AppId、AppKey、AppSecret；2. AppSecret 仅服务端签名用（sign = md5(appId + appKey + timestamp + appSecret)）。⚠️ vivo 的 AppId / AppKey 客户端那份**必须另外写进 android/local.properties**（vivo.push.appId / vivo.push.appKey）——vivo SDK 从 AndroidManifest 读，无法运行时下发，**换凭据必须重新出包**。',
  },
  {
    key: 'xiaomi',
    label: '小米 / 红米',
    onlyKeys: ['xiaomiAppId', 'xiaomiAppKey', 'xiaomiAppSecret', 'xiaomiPackageName'],
    guide:
      '1. 小米开放平台 → 推送 → 应用信息，取 AppId、AppKey、AppSecret；2. AppSecret 仅服务端鉴权用（Authorization: key=xxx），与客户端的 AppKey **不是同一个值**。小米服务端单推强制校验 restricted_package_name，必须与 Android 实际包名一致（默认已填），否则整批拒收。',
  },
  {
    key: 'apns',
    label: 'iOS（Apple APNs）',
    onlyKeys: ['apnsTeamId', 'apnsKeyId', 'apnsP8Key', 'apnsBundleId'],
    guide:
      'iOS 这一组不是某家厂商的应用凭据，而是 **Apple 开发者账号级密钥**：设备侧的长连接由系统自带、没有第三方 SDK，服务端只把通知交给 Apple 的 APNs。1. developer.apple.com 入 Apple Developer Program（公司主体要 D-U-N-S 号，688 元/年，资质审核几天到两周）；2. 门户 → Certificates, Identifiers & Profiles → Identifiers，给 iOS 的 App ID 勾上 Push Notifications；3. Keys → 新建一条「Apple Push Notifications service (APNs)」密钥，下载 .p8 并记下页面给的 Key ID；4. Membership 页取 Team ID；5. 四个值填下方，Topic 必须与 iOS 工程的 Bundle Identifier 逐字一致。⚠️ .p8 **只下载一次、门户不再重显**，私钥不要发进聊天或贴进工单；它覆盖该 Team 下所有 App 与所有环境、本身不过期，可随时撤销——撤销后所有 iOS 设备立刻 403。⚠️ 填错的定位：Team ID / Key ID / .p8 不成对 → 整批 403 InvalidProviderToken；Topic 与 Bundle ID 不一致 → 整批 400 DeviceTokenNotForTopic；这两种代码都不停用任何令牌。⚠️ 服务端已就绪，iOS 客户端还没上报设备令牌（本机 Windows 无法编译验证那一段），所以现在填上也不会有一条推送发出，补齐步骤见 docs/部署指南.md §11.7。',
  },
];

export default function CredentialsPage() {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              <KeyOutlined style={{ color: '#0d9488', marginRight: 8 }} />
              第三方服务凭据
            </div>
            <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
              集中维护各服务商密钥（加密存储）。六类凭据按上方标签页分开维护（邮件 / 短信 / 实名认证 / 推送 / 地图 / 实时通知），同一类里若有多个运营商或通道再按小标签切换。邮件 SMTP 的全部连接参数已收进「邮件 SMTP」标签页一处配好；短信、实名认证等只需在此填密钥，业务开关、签名、模板在注册流程页按步骤配置。
            </div>
          </div>
          <Space>
            <Button icon={<SafetyCertificateOutlined />} onClick={() => navigate('/settings/register-flow')}>注册流程</Button>
            <Button onClick={() => navigate('/settings')}>返回</Button>
          </Space>
        </div>
      </Card>

      <Card styles={{ body: { paddingTop: 8 } }}>
        <Tabs
          items={[
            {
              key: 'smtp',
              label: (
                <span>
                  <MailOutlined /> 邮件 SMTP
                </span>
              ),
              children: <SmtpCredentialPanel />,
            },
            {
              key: 'sms',
              label: (
                <span>
                  <MessageOutlined /> 短信服务
                </span>
              ),
              children: (
                <ProviderTabsSection
                  configName="sms"
                  intro="配置阿里云短信 AccessKey / Secret。配好后到注册流程页「手机号持有验证」步选择该服务商即可发送短信。"
                  providers={SMS_PROVIDERS}
                />
              ),
            },
            {
              key: 'identity',
              label: (
                <span>
                  <IdcardOutlined /> 实名认证
                </span>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <ProviderTabsSection
                    configName="identity"
                    intro="按运营商分别配置核验密钥。配好后到注册流程页「实名核验」步选择对应服务商并开启实名认证即可生效；用下方「测试这条渠道是否可用」可以在不注册的前提下先验证一遍通路。"
                    providers={IDENTITY_PROVIDERS}
                  />
                  <IdentityChannelTestCard />
                </div>
              ),
            },
            {
              key: 'push',
              label: (
                <span>
                  <MobileOutlined /> 推送服务
                </span>
              ),
              children: (
                <ProviderTabsSection
                  configName="push"
                  intro="App 进程被系统回收后，只有系统级的长连接还能把通知送到状态栏：安卓按 ROM 走五家厂商、鸿蒙走华为 Push Kit、iOS 走 Apple 官方 APNs。这里按「厂商 + 端」分别维护凭据，App 端会按 ROM 自动选用对应通道；某一家的凭据没配，那类设备就收不到离线推送，其余通道互不影响。全部未配置时推送静默降级，不影响业务。"
                  providers={PUSH_PROVIDERS}
                />
              ),
            },
            {
              key: 'amap',
              label: (
                <span>
                  <EnvironmentOutlined /> 地图服务
                </span>
              ),
              children: (
                <CredentialSection
                  name="amap"
                  onlyKeys={['webKey']}
                  desc="高德地图 Web 服务 Key：订单活动地址点「导航」时，服务端先把地址文本解析成经纬度，地图 App 的终点才能自动落位（高德路线规划只认经纬度，光有地址终点栏是空的）。在高德开放平台 lbs.amap.com → 控制台 → 应用管理 → 创建应用（平台类型选「Web 服务」）→ 添加 Key 后填入下方。个人认证 15 万次/月免费，超出 30 元/万次、不会静默扣费；对外商用需另购技术服务许可。未配置时各端导航退化为「按地址关键词检索」，功能不受影响。"
                />
              ),
            },
            {
              key: 'notify',
              label: (
                <span>
                  <NotificationOutlined /> 实时通知
                </span>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <NotifyEventSwitch />
                  <ProviderTabsSection
                    configName="notify"
                    intro="运营侧实时推送：官网「联系我们」新留言、新的人工审核待审申请、会员银行转账待核对。先在上「通知事件」勾选要推哪些，再在下方各通道填 Webhook / 邮箱并开启——被勾选事件会共用同一套已启用通道。四条通道相互独立、可多选并用；全部关闭时业务照常入库，只是不额外推送。"
                    providers={NOTIFY_CHANNELS}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
